import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { Close } from '@mui/icons-material'
import { Trans, useTranslation } from 'gatsby-plugin-react-i18next'
import React, { useRef, useEffect } from 'react'
import LinkButton from '../../components/Link/LinkButton'
import useSiteMetadata from '../../hooks/useSiteMetadata'
import OopsPng from '../../images/oops.png'
import { WidgetTRefs } from '../../types/widget'
import { useMpEventType } from '../../utils/mixpanel'
import { useBETelemetry } from '../../hooks/useTelemetry'

interface Props {
  open: boolean
  onClose: () => void
  status: string
}
const ResumeUploadStatusV2: React.FC<Props> = ({ open, onClose, status }) => {
  const { classes, cx } = useStyles()
  const { t, i18n } = useTranslation()
  const { defaultResumeProduct } = useSiteMetadata()
  const ctaUrlPrefix = t(`${WidgetTRefs.StatusModalTRef}.success.ctaUrl`)
  // Check if the current language is French
  const isFrench = i18n.language === 'fr'
  // Define the dynamic part
  const dynamicPart = isFrench ? '' : '/{{lowercased brand.flagshipProduct}}'
  // Concatenate the parts together
  const completeUrl = `${dynamicPart}${ctaUrlPrefix}`
  const telemetry = useBETelemetry()
  const modalTracked = useRef(false)

  const handleButtonClick = () => {
    telemetry.track({
      event: 'click_review_thanks_discover_packages',
    })
  }

  useEffect(() => {
    if (open && status ==='success' && !modalTracked.current) {
      telemetry.track({
        event: 'view_review_thanks_modal',
        properties: {
          timestamp: Date.now() / 1000,
          url: window.location.href,
        },
      })
      modalTracked.current = true;
    }
  }, [status, modalTracked, telemetry])

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="resume-upload-dialog-title"
      aria-describedby="resume-upload-dialog-description"
      classes={{ paper: classes.dialog }}
    >
      <IconButton
        className={classes.closeButton}
        onClick={onClose}
        size="large"
      >
        <Close fontSize="small" />
      </IconButton>
      <DialogTitle className={classes.heading} id="resume-upload-dialog-title">
        {status === 'error' && (
          <img
            src={OopsPng}
            alt={t(`${WidgetTRefs.StatusModalTRef}.${status}.imgAlt`)}
          />
        )}
      </DialogTitle>
      <DialogContent className={classes.customDialogContent}>
        <DialogContentText id="resume-upload-dialog-description">
          <Typography variant="h2" className={classes.customDialogContent}>
            {t(`${WidgetTRefs.StatusModalTRef}.${status}.heading`)}
          </Typography>
          <Trans
            i18nKey={`${WidgetTRefs.StatusModalTRef}.${status}.body`}
            values={{
              defaultResumeProduct,
            }}
            components={{
              strong: <strong />,
            }}
          />
        </DialogContentText>
      </DialogContent>
      {status === 'success' && (
        <DialogActions
          className={cx(
            classes.customDialogActions,
            classes.customDialogContent
          )}
        >
          <LinkButton
            to={completeUrl}
            className="mp_cta"
            onClick={handleButtonClick}
          >
            {t(`${WidgetTRefs.StatusModalTRef}.success.ctaText`)}
          </LinkButton>
        </DialogActions>
      )}
    </Dialog>
  )
}

const useStyles = makeStyles()((theme) => ({
  dialog: {
    margin: '0.75rem',
  },
  customDialogActions: {
    justifyContent: 'center',
  },
  heading: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '1rem',
    background: '#EAF6FF',
    padding: '2.0rem',
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    color: theme.palette.secondary.dark,
    '&:focus': {
      outline: 0,
    },
  },
  customDialogContent: {
    textAlign: 'center',
  },
}))

export default ResumeUploadStatusV2
